/**
 * The questions that change the answer — ONE set, asked twice (UX review, 17 Oct 2026, version A).
 *
 * Step 3 asks them inline before the plans run, pre-filled with what we would suggest and why;
 * "Things that change the answer" under the plans reopens the same five. Each question is in the
 * reader's words with the suggested value and the reason, so a family member can accept every
 * suggestion and press on without understanding any of it, and an adviser can change any of them.
 *
 * The engine inputs do not change: the answers map onto the analyser's setup bag
 * (coupleValidation) and the engine's settings through the adapters at the foot of this file.
 */
import React from 'react';
import { formatCurrency, parseCurrency, calculateAge } from '../../utils/index.ts';
import StampDutyFields from '../../components/ui/StampDutyFields.tsx';
import { MIN_EMERGENCY_FUND } from '../../utils/emergencyFund.ts';
import { careYearsFor } from './shortfallOutlook.ts';
import { collectLiquidAssetsForSuggestionRad, resolveSeEmergencyFund } from '../../utils/suggestionEngineFunding.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import GuaranteedIncomeQuestions, { goalsForAnswer, type GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { annuityPreferenceOf } from '../../../../shared/src/calculation/annuityPreference.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { QuestionTitle } from '../../components/strategy/QuestionParts.tsx';

export interface EngineAnswers {
    /** How many years of care the estate figure looks ahead. */
    years: number;
    /** Money the plans must leave in the bank. */
    emergencyFund: number;
    /** Whether selling the family home is tested (the engine's downsize scenarios). */
    testSellingHome: boolean;
    /** Whether renting the home out is tested; false = "keep it, and don't rent it out" (25 Oct 2026). */
    testRentingHome: boolean;
    newHomeValue: number;
    newHomeState?: string;
    stampDuty?: number;
    sellingCosts: number;
    /** What the partner would pay to rent elsewhere if the home were let out (a year). */
    partnerAnnualRent: number;
    /** Model the partner entering care later. */
    partnerLater: boolean;
    /** Projection year the partner enters (1 = the first year). */
    partnerEntryYear: number;
    partnerFutureRoomPrice: number;
    originalEntrantAlive: boolean;
    /**
     * The guaranteed income answers given HERE, not yet written to the client's goals (null when the question
     * was left alone). They belong to the client, not to this run (annuityPreference.ts), so the screen that
     * submits merges them into goals; nothing else writes the annuity goals, and an answer given in another
     * module is never overwritten by a default (guaranteed income review, 21 Oct 2026).
     */
    annuityGoals?: GuaranteedIncomeGoals | null;
    /** Money held in a trust or company: may the plans use it for the deposit? */
    structureFunding?: 'include' | 'exclude';
}

export interface EngineQuestionsProps {
    value: EngineAnswers;
    onChange: (next: EngineAnswers) => void;
    factFind: DraftFactFind;
    /** Living costs once in care (a year), for the emergency-money suggestion. */
    scenarioLivingExpenses: number;
    /** Field ids are prefixed so the inline and dialog copies never share one. */
    idPrefix?: string;
    /** The room's full deposit (RAD). No longer taken off the guaranteed income figure: the plans test every deposit level. */
    roomDeposit?: number;
}

const MADE_OF_WORDS: Record<string, string> = {
    bankAccounts: 'bank accounts',
    termDeposits: 'term deposits',
    shares: 'shares',
    accountBasedPensions: 'account-based pension',
};

/**
 * The money that could go into a guaranteed income, for the share question: the savings and investments the
 * plans may draw on (collectLiquidAssetsForSuggestionRad: the bank accounts net of the emergency money, term
 * deposits, shares and account-based pensions, respecting the trust and company answer), less the room
 * deposit when it is known. When it is not, the words say the deposit is left aside rather than inventing one.
 */
export function annuityInvestableFor(factFind: DraftFactFind, emergencyFund: number, roomDeposit?: number | null): { investable: number; lead: string; madeOf: string } {
    // An empty answer is the engine's own figure (live review, 22 Oct 2026: "the $0 kept for emergencies").
    const ef = Number(emergencyFund) > 0 ? Number(emergencyFund) : resolveSeEmergencyFund(factFind);
    const assets = collectLiquidAssetsForSuggestionRad(factFind, ef);
    const total = assets.reduce((t, a) => t + (Number(a.value) || 0), 0);
    const deposit = Math.max(0, Number(roomDeposit) || 0);
    const kinds = [...new Set(assets.map((a) => MADE_OF_WORDS[a.type] || null).filter((x): x is string => !!x))];
    const madeOf = kinds.length === 0 ? 'savings and investments'
        : kinds.length === 1 ? kinds[0]
            : `${kinds.slice(0, -1).join(', ')} and ${kinds[kinds.length - 1]}`;
    const emergency = formatCurrency(ef);
    return deposit > 0
        ? { investable: Math.max(0, total - deposit), lead: `After the room deposit and the ${emergency} kept for emergencies`, madeOf }
        : { investable: total, lead: `Before anything is paid towards the room, and after the ${emergency} kept for emergencies`, madeOf };
}

/** Said under the share in the tested-plans question: the share is of what each plan leaves. */
export const ANNUITY_SHARE_NOTE = 'A plan that pays some of the room up front leaves less, and the share is then of what is left.';

/** The whole-dollar box the mock-up draws: "$50,000", typed freely, kept as a number. */
const MoneyBox = ({ id, value, onChange, className = '' }: { id: string; value: number; onChange: (n: number) => void; className?: string }) => (
    <input
        id={id}
        type="text"
        inputMode="numeric"
        value={value > 0 ? formatCurrency(value) : ''}
        onChange={(e) => onChange(Math.max(0, parseFloat(String(parseCurrency(e.target.value))) || 0))}
        placeholder="$0"
        className={`rounded-lg border border-slate-300 px-3 py-2 text-[15px] font-semibold tabular-nums text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${className}`}
    />
);

const Radio = ({ name, checked, onSelect, children }: { name: string; checked: boolean; onSelect: () => void; children: React.ReactNode }) => (
    <label className="inline-flex cursor-pointer items-center gap-2 text-[14px] text-slate-800">
        <input type="radio" name={name} checked={checked} onChange={onSelect} className="h-4 w-4 accent-blue-600" />
        {children}
    </label>
);

/** Numbered, as every module's questions are (owner, 23 Oct 2026: aged care's were the only ones without). */
const Question = ({ n, label, children, testId }: { n: number; label: React.ReactNode; children: React.ReactNode; testId?: string }) => (
    <div data-testid={testId}>
        <QuestionTitle n={n} as="p">{label}</QuestionTitle>
        <div className="mt-2">{children}</div>
    </div>
);

const Hint = ({ children }: { children: React.ReactNode }) => <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{children}</p>;

export const EngineQuestions = ({ value, onChange, factFind, scenarioLivingExpenses, idPrefix = 'eq' }: EngineQuestionsProps) => {
    const set = (patch: Partial<EngineAnswers>) => onChange({ ...value, ...patch });
    const goals = (factFind.goals || {}) as { aged_care_for?: string };
    const isCouple = factFind.adviceFor === 'couple';
    const bothInCare = isCouple && goals.aged_care_for === 'both';
    const partnerEntering = goals.aged_care_for === 'partner';
    const entering = partnerEntering ? factFind.partner : factFind.client;
    const staying = partnerEntering ? factFind.client : factFind.partner;
    const enteringName = String(entering?.firstName || 'the person entering care').trim();
    const stayingName = isCouple ? String(staying?.firstName || 'your partner').trim() : null;
    const age = entering?.dob ? calculateAge(entering.dob) : null;
    const health = String(entering?.healthStatus || '').trim().toLowerCase();
    const suggestedYears = careYearsFor(factFind as never)?.years ?? 5;
    const sixMonths = Math.round((Number(scenarioLivingExpenses) || 0) / 2);
    const suggestedEmergency = Math.max(MIN_EMERGENCY_FUND, sixMonths);
    const hasHome = (Number(factFind.home?.value) || 0) > 0;
    const hasStructures = (factFind.structures || []).length > 0;
    const thisYear = new Date().getFullYear();
    // The question reads the client's answer with any change made here on top, until the screen submits.
    const factFindWithAnswer = value.annuityGoals ? { ...factFind, goals: { ...(factFind.goals || {}), ...value.annuityGoals } } as DraftFactFind : factFind;
    /*
     * BEFORE THE ROOM IS PAID (owner, 22 Oct 2026: "$0 … presupposes we will definitely pay the money as a RAD").
     * The plans test paying none, half, three quarters or all of the room up front, and a guaranteed income is
     * sized as a share of what each leaves (agedCareMeansTestCandidates: the headroom on each base). So the
     * figure is the money before anything goes to the room, and the note says a deposit leaves less.
     */
    const investable = annuityInvestableFor(factFind, value.emergencyFund);
    const yearsId = `${idPrefix}-years`;
    const emergencyId = `${idPrefix}-emergency`;
    // Numbered in the order shown; EngineSetupInline's "Five things…" counts the same questions.
    let asked = 0;
    const next = () => { asked += 1; return asked; };

    return (
        <div className="space-y-6" data-testid="engine-questions">
            <Question n={next()} label="How many years of care should we plan for?" testId={`${idPrefix}-q-years`}>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        id={yearsId}
                        type="number"
                        min={1}
                        max={10}
                        value={value.years}
                        onChange={(e) => set({ years: Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                        className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-[15px] font-semibold text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor={yearsId} className="text-[13px] text-slate-600">years</label>
                </div>
                <Hint>
                    We suggest <strong className="text-slate-800">about {suggestedYears}</strong>, based on {enteringName}&apos;s age{age ? ` (${age})` : ''} and health{health ? ` (recorded as ${health})` : ''}. Nobody knows how long care will be needed. The &quot;left to the family&quot; figures use this, and you can change it later to see whether the answer changes.
                </Hint>
            </Question>

            <Question n={next()} label="How much should stay in the bank for emergencies?" testId={`${idPrefix}-q-emergency`}>
                <MoneyBox id={emergencyId} value={value.emergencyFund} onChange={(n) => set({ emergencyFund: n })} className="w-40" />
                <Hint>
                    The plans will not spend this on the deposit. We suggest the greater of six months&apos; living costs ({formatCurrency(sixMonths)}) or {formatCurrency(MIN_EMERGENCY_FUND)}
                    {Math.round(value.emergencyFund) !== suggestedEmergency && (
                        <> — <button type="button" onClick={() => set({ emergencyFund: suggestedEmergency })} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">use {formatCurrency(suggestedEmergency)}</button></>
                    )}.
                </Hint>
            </Question>

            {hasHome && (
                <Question n={next()} label="What happens to the family home?" testId={`${idPrefix}-q-home`}>
                    <p className="mb-2 text-[13px] leading-relaxed text-slate-600">
                        {bothInCare
                            ? <>With {enteringName} and {stayingName} both in care, nobody is left living in the family home. Keeping it is always tested; say whether renting it out or selling it should be tested too.</>
                            : isCouple
                                ? <>When {enteringName} moves into care, <strong className="text-slate-800">{stayingName} stays in the family home</strong>. Keeping it is always tested; say whether renting it out or selling it should be tested too, so you can see the trade-off.</>
                                : <>With {enteringName} in care, the family home can be kept, let out, or sold. Keeping it is always tested; say whether renting it out or selling it should be tested too.</>}
                    </p>
                    {/* One question, three plain answers (owner, 25 Oct 2026: a client who will keep the home and not let
                        it — for family, say — had no way to say so, and rent-out plans were always tested). */}
                    <div className="flex flex-col gap-2" data-testid={`${idPrefix}-home-choice`}>
                        <Radio name={`${idPrefix}-home`} checked={value.testRentingHome && !value.testSellingHome} onSelect={() => set({ testRentingHome: true, testSellingHome: false })}>
                            Keep it — also test renting it out
                        </Radio>
                        <Radio name={`${idPrefix}-home`} checked={!value.testRentingHome} onSelect={() => set({ testRentingHome: false, testSellingHome: false })}>
                            Keep it, and don&apos;t rent it out
                        </Radio>
                        <Radio name={`${idPrefix}-home`} checked={value.testRentingHome && value.testSellingHome} onSelect={() => set({ testRentingHome: true, testSellingHome: true })}>
                            {isCouple && !bothInCare ? `${stayingName} stays — compare keeping, renting out and selling` : 'Compare keeping, renting it out and selling'}
                        </Radio>
                    </div>
                    {value.testSellingHome && (
                        <details className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3" data-testid={`${idPrefix}-sale-details`}>
                            <summary className="cursor-pointer text-[13px] font-semibold text-blue-700">Price the sale — rough numbers are fine</summary>
                            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {!bothInCare && (
                                    <div>
                                        <label htmlFor={`${idPrefix}-newhome`} className="block text-[13px] font-semibold text-slate-800">New home value, if {stayingName ? `${stayingName} downsizes` : 'downsizing'}</label>
                                        <MoneyBox id={`${idPrefix}-newhome`} value={value.newHomeValue} onChange={(n) => set({ newHomeValue: n })} className="mt-1 w-full" />
                                        <p className="mt-1 text-[12px] text-slate-500">What a smaller home nearby would cost.</p>
                                    </div>
                                )}
                                {!bothInCare && (
                                    <StampDutyFields
                                        state={value.newHomeState}
                                        onStateChange={(v) => set({ newHomeState: v })}
                                        newHomePrice={value.newHomeValue}
                                        duty={value.stampDuty}
                                        onDutyChange={(v) => set({ stampDuty: v })}
                                        labelClassName="block text-[13px] font-semibold text-slate-800"
                                    />
                                )}
                                <div>
                                    <label htmlFor={`${idPrefix}-selling`} className="block text-[13px] font-semibold text-slate-800">Selling costs</label>
                                    <MoneyBox id={`${idPrefix}-selling`} value={value.sellingCosts} onChange={(n) => set({ sellingCosts: n })} className="mt-1 w-full" />
                                    <p className="mt-1 text-[12px] text-slate-500">Agent, legal and moving costs, in dollars.</p>
                                </div>
                                {isCouple && !bothInCare && (
                                    <div>
                                        <label htmlFor={`${idPrefix}-rent`} className="block text-[13px] font-semibold text-slate-800">Rent a year, if {stayingName} rents elsewhere</label>
                                        <MoneyBox id={`${idPrefix}-rent`} value={value.partnerAnnualRent} onChange={(n) => set({ partnerAnnualRent: n })} className="mt-1 w-full" />
                                        <p className="mt-1 text-[12px] text-slate-500">Used when the home is let out and {stayingName} lives elsewhere.</p>
                                    </div>
                                )}
                            </div>
                        </details>
                    )}
                </Question>
            )}

            {isCouple && !bothInCare && (
                <Question n={next()} label={`If ${stayingName} later needs care too`} testId={`${idPrefix}-q-partner`}>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <Radio name={`${idPrefix}-partner`} checked={!value.partnerLater} onSelect={() => set({ partnerLater: false })}>Don&apos;t model this</Radio>
                        <Radio name={`${idPrefix}-partner`} checked={value.partnerLater} onSelect={() => set({ partnerLater: true })}>
                            Model {stayingName} entering care in …
                        </Radio>
                        {/* Capped at the last year planned for: the overlay has no year to apply to beyond the
                            horizon and used to vanish without a word (owner-caught, 18 Oct 2026: a saved 2031
                            against a 5-year plan showed one card and no partner-later card). */}
                        <input
                            type="number"
                            aria-label={`Year ${stayingName} enters care`}
                            min={thisYear}
                            max={thisYear + Math.max(1, value.years) - 1}
                            value={thisYear + Math.max(1, value.partnerEntryYear) - 1}
                            onChange={(e) => {
                                const y = parseInt(e.target.value, 10);
                                if (!Number.isFinite(y)) return;
                                set({ partnerLater: true, partnerEntryYear: Math.min(Math.max(1, value.years), Math.max(1, y - thisYear + 1)) });
                            }}
                            className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-[15px] font-semibold text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    {value.partnerLater && value.partnerEntryYear > value.years && (
                        <p className="mt-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-[13px] leading-relaxed text-slate-700" data-testid={`${idPrefix}-partner-beyond`}>
                            {thisYear + value.partnerEntryYear - 1} is after the {value.years} {value.years === 1 ? 'year' : 'years'} we plan for, so it would not change these plans. Pick an earlier year, or plan for more years above.
                        </p>
                    )}
                    {value.partnerLater && value.partnerEntryYear <= value.years && (
                        <Hint>Within the {value.years} years we plan for. A plan that still looks best once {stayingName} is in care too is shown as its own card.</Hint>
                    )}
                    {value.partnerLater && (
                        <div className="mt-3 grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 sm:grid-cols-2">
                            <div>
                                <label htmlFor={`${idPrefix}-partner-room`} className="block text-[13px] font-semibold text-slate-800">{stayingName}&apos;s room price</label>
                                <MoneyBox id={`${idPrefix}-partner-room`} value={value.partnerFutureRoomPrice} onChange={(n) => set({ partnerFutureRoomPrice: n })} className="mt-1 w-full" />
                                <p className="mt-1 text-[12px] text-slate-500">The room {stayingName} would take; the same price as {enteringName}&apos;s unless you know otherwise.</p>
                            </div>
                            <div>
                                <p className="text-[13px] font-semibold text-slate-800">Is {enteringName} still in care then?</p>
                                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                                    <Radio name={`${idPrefix}-alive`} checked={value.originalEntrantAlive} onSelect={() => set({ originalEntrantAlive: true })}>Yes — two rooms at once</Radio>
                                    <Radio name={`${idPrefix}-alive`} checked={!value.originalEntrantAlive} onSelect={() => set({ originalEntrantAlive: false })}>No — {enteringName}&apos;s deposit has come back</Radio>
                                </div>
                            </div>
                        </div>
                    )}
                </Question>
            )}

            {/* THE GUARANTEED INCOME QUESTION (guaranteed income review, 21 Oct 2026; mock-up screens 1 and 2): the
                one component every module asks, with the explanation always on screen, three answers (money
                back suggested, income only only on request, no) and the share. It used to offer "Show me
                whichever works best" or "No" and save over the client's earlier answer. */}
            <div className="border-t border-slate-200 pt-6" data-testid={`${idPrefix}-q-annuity`}>
                <GuaranteedIncomeQuestions
                    number={next()}
                    factFind={factFindWithAnswer}
                    onGoals={(patch) => set({ annuityGoals: { ...(value.annuityGoals || {}), ...patch } })}
                    personName={bothInCare || !entering?.firstName || (!isCouple && householdVoice(factFind).you) ? undefined : enteringName}
                    investable={investable.investable}
                    investableLead={investable.lead}
                    investableMadeOf={investable.madeOf}
                    investableNote={ANNUITY_SHARE_NOTE}
                    inCare
                    idPrefix={idPrefix}
                />
            </div>

            {hasStructures && (
                <Question n={next()} label="Money held in a family trust or company — may the plans use it for the deposit?" testId={`${idPrefix}-q-structures`}>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                        <Radio name={`${idPrefix}-structures`} checked={value.structureFunding === 'include'} onSelect={() => set({ structureFunding: 'include' })}>Yes — test it</Radio>
                        <Radio name={`${idPrefix}-structures`} checked={value.structureFunding !== 'include'} onSelect={() => set({ structureFunding: 'exclude' })}>No — personal money only</Radio>
                    </div>
                    <Hint>Either way, what the trust or company holds still counts for the pension and the care fees.</Hint>
                </Question>
            )}
        </div>
    );
};

/* ---------- Adapters: the one set of answers, read from and written to the engine's two stores ---------- */

/** The analyser's setup bag, as this file reads it. */
export interface SetupBag {
    exploreAlternatives?: boolean;
    downsizeNewHomeValue?: number;
    sellingCosts?: number;
    stampDuty?: number;
    newHomeState?: string;
    partnerAnnualRent?: number;
    enablePartnerFutureEvent?: boolean;
    partnerEntryYear?: number;
    partnerFutureRoomPrice?: number;
    originalEntrantAliveAtPartnerEntry?: boolean;
    emergencyFund?: number;
    suggestionEngineSetupComplete?: boolean;
    [key: string]: unknown;
}

const num = (v: unknown, fallback: number): number => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
};

/** ~70% of the current home, the "downsize but stay in the area" default every screen has used. */
export function defaultNewHomeValue(factFind: DraftFactFind): number {
    const home = Number(factFind.home?.value) || 0;
    return home > 0 ? Math.round(home * 0.7) : 250000;
}

export function answersFromSetup(bag: SetupBag | null | undefined, factFind: DraftFactFind, legacyYears: number, scenarioLivingExpenses: number, defaultPartnerRoomPrice: number): EngineAnswers {
    const b = bag || {};
    const sixMonths = Math.round((Number(scenarioLivingExpenses) || 0) / 2);
    // A first visit tests selling the home (the setup pop-over's "explore all possibilities" default);
    // a later visit keeps whatever was answered.
    const answered = b.suggestionEngineSetupComplete === true;
    const testSellingHome = answered ? (Number(b.downsizeNewHomeValue) || 0) > 0 : (b.exploreAlternatives !== false);
    return {
        years: suggestedOrSetYears(factFind, legacyYears, answered),
        emergencyFund: b.emergencyFund != null && Number.isFinite(Number(b.emergencyFund)) ? Number(b.emergencyFund) : Math.max(MIN_EMERGENCY_FUND, sixMonths),
        testSellingHome,
        testRentingHome: b.testRentingHome !== false,
        newHomeValue: num(b.downsizeNewHomeValue, defaultNewHomeValue(factFind)),
        newHomeState: typeof b.newHomeState === 'string' ? b.newHomeState : undefined,
        stampDuty: Number(b.stampDuty) || undefined,
        sellingCosts: Number(b.sellingCosts) || 0,
        partnerAnnualRent: num(b.partnerAnnualRent, 40000),
        partnerLater: b.enablePartnerFutureEvent === true,
        partnerEntryYear: num(b.partnerEntryYear, 3),
        partnerFutureRoomPrice: num(b.partnerFutureRoomPrice, defaultPartnerRoomPrice || 500000),
        originalEntrantAlive: b.originalEntrantAliveAtPartnerEntry !== false,
        annuityGoals: null,
        structureFunding: (factFind.structureFundingInSuggestions as 'include' | 'exclude' | undefined) || 'exclude',
    };
}

/**
 * THE PLANNING PERIOD STARTS FROM THE SUGGESTION (guaranteed income review E9, 21 Oct 2026). The question says
 * "we suggest about N years" from the person's age and health (careYearsFor), but the box opened at the
 * analyser's default of 5, so a client who accepted every suggestion planned for a period we did not suggest.
 * Until the questions have been answered once, the box opens at the suggestion; after that, at what was set.
 */
export function suggestedOrSetYears(factFind: DraftFactFind, legacyYears: number, answered: boolean): number {
    const suggested = careYearsFor(factFind as never)?.years;
    const raw = !answered && suggested != null && suggested > 0 ? suggested : Number(legacyYears) || 5;
    return Math.min(10, Math.max(1, Math.round(raw)));
}

export function setupFromAnswers(bag: SetupBag | null | undefined, a: EngineAnswers): SetupBag {
    return {
        ...(bag || {}),
        exploreAlternatives: a.testSellingHome,
        testRentingHome: a.testRentingHome,
        downsizeNewHomeValue: a.testSellingHome ? a.newHomeValue : 0,
        sellingCosts: a.sellingCosts,
        stampDuty: a.stampDuty,
        newHomeState: a.newHomeState,
        partnerAnnualRent: a.partnerAnnualRent,
        enablePartnerFutureEvent: a.partnerLater,
        partnerEntryYear: a.partnerEntryYear,
        partnerFutureRoomPrice: a.partnerFutureRoomPrice,
        originalEntrantAliveAtPartnerEntry: a.originalEntrantAlive,
        emergencyFund: a.emergencyFund,
        suggestionEngineSetupComplete: true,
    };
}

/**
 * The guaranteed income goals to merge into the client's goals: only what was answered here (null when the
 * question was left alone), so a submit never overwrites the client's answer with a default.
 */
export function annuityGoalsFromAnswers(a: EngineAnswers, factFind?: DraftFactFind | null): GuaranteedIncomeGoals | null {
    if (a.annuityGoals && Object.keys(a.annuityGoals).length > 0) return a.annuityGoals;
    /*
     * GOING ON WITH THE SUGGESTED ANSWER IS AN ANSWER (owner, 22 Oct 2026: "Show me the plans" did nothing).
     * The plans wait for an answer on the file (AgedCareAnalyser engineSetupDone), but a client who accepted
     * the suggested "Yes, if the money goes back" wrote nothing, so the button returned to the same form.
     * On a file never answered, the suggestion shown is written, dated for the record. An answer already on
     * the file, from any module, is still never overwritten.
     */
    if (factFind && !annuityPreferenceOf(factFind as never).answered) return goalsForAnswer('back');
    return null;
}

/** The engine's settings shape (AgedCareSuggestionEnginePanel.SuggestionEngineSettings), as this file reads it. */
export interface EngineSettingsLike {
    legacyYears: number;
    emergencyFund: number;
    newHomeValue: number;
    annualRentCost: number;
    sellingCosts: number | undefined;
    stampDuty?: number;
    newHomeState?: string;
    enablePartnerFutureEvent: boolean;
    partnerEntryYear: number;
    partnerFutureRoomPrice: number | undefined;
    originalEntrantAliveAtPartnerEntry: boolean;
    structureFundingInSuggestions?: 'include' | 'exclude';
    [key: string]: unknown;
}

export function answersFromSettings(s: EngineSettingsLike, factFind: DraftFactFind, testSellingHome: boolean): EngineAnswers {
    return {
        years: Math.min(10, Math.max(1, Math.round(Number(s.legacyYears) || 5))),
        emergencyFund: Number(s.emergencyFund) || 0,
        testSellingHome,
        testRentingHome: s.testRentingHome !== false,
        newHomeValue: num(s.newHomeValue, defaultNewHomeValue(factFind)),
        newHomeState: s.newHomeState,
        stampDuty: Number(s.stampDuty) || undefined,
        sellingCosts: Number(s.sellingCosts) || 0,
        partnerAnnualRent: num(s.annualRentCost, 40000),
        partnerLater: s.enablePartnerFutureEvent === true,
        partnerEntryYear: num(s.partnerEntryYear, 3),
        partnerFutureRoomPrice: num(s.partnerFutureRoomPrice, 500000),
        originalEntrantAlive: s.originalEntrantAliveAtPartnerEntry !== false,
        annuityGoals: null,
        structureFunding: s.structureFundingInSuggestions || (factFind.structureFundingInSuggestions as 'include' | 'exclude' | undefined) || 'exclude',
    };
}

export function settingsFromAnswers<T extends EngineSettingsLike>(s: T, a: EngineAnswers): T {
    return {
        ...s,
        legacyYears: a.years,
        emergencyFund: a.emergencyFund,
        // The engine tests selling the home only while a new-home value is set (downsizeEnabled).
        newHomeValue: a.testSellingHome ? a.newHomeValue : 0,
        testRentingHome: a.testRentingHome,
        annualRentCost: a.partnerAnnualRent,
        sellingCosts: a.sellingCosts,
        stampDuty: a.stampDuty,
        newHomeState: a.newHomeState,
        enablePartnerFutureEvent: a.partnerLater,
        partnerEntryYear: a.partnerEntryYear,
        partnerFutureRoomPrice: a.partnerFutureRoomPrice,
        originalEntrantAliveAtPartnerEntry: a.originalEntrantAlive,
        structureFundingInSuggestions: a.structureFunding,
    };
}

export default EngineQuestions;
